import React from 'react';

import MaskedDialog from 'pa-react/dist/components/maskedDialog/MaskedDialog';

import './MaskedAppDialog.css';
import { EVENT } from 'constants/event';
import { useEventBus } from 'hooks/event/useEventBus';

//
// --- Helper ---

/**
 * @typedef PAMaskedDialogButtonProps
 * @property {function} onClick = callback triggered on button click
 * @property {string} text - button label text
 */

//
// --- MaskedAppDialog ---

/**
 * Display an overlay covering the entire supplier search application. Takes in a
 * message string and an optional array of objects that define props for pa-react
 * `<Button />` components.
 *
 * Opening the dialog is done through eventBusClient sendEvent calls. Closing the dialog
 * is handled automatically when a button on the dialog is clicked. Displaying a button
 * is currently a requirement for this component. If no buttons are provided, an "OK"
 * button will be displayed.
 *
 *   Example Use 1: (Message with ok button)
 *   ```
 *     eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, { message: "A message." });
 *   ```
 *
 *   Example Use 2: (Message with defined button)
 *   ```
 *     eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
 *         buttons: [{ onClick: () => {}, text: "Button Label"}],
 *         message: "A message."
 *     });
 *   ```
 *
 *   Example Use 3: (Cancelable dialog - automatically appends a cancel button to buttons provided)
 *  *   ```
 *     eventBusClient.sendEvent(EVENT.DISPLAY_CANCELABLE_MASKED_APP_DIALOG, {
 *         buttons: [{ onClick: () => {}, text: "Affirm Button"}],
 *         message: "Are you sure?"
 *     });
 *   ```
 */
const MaskedAppDialog = () => {
  /** MaskedDialog expects an array of "prop objects", only read `onClick` and `text` props */
  const [buttonPropsCollection, setButtonPropsCollection] = React.useState([]);
  const [message, setMessage] = React.useState();

  useEventBus({
    [EVENT.DISPLAY_CANCELABLE_MASKED_APP_DIALOG]: event => handleDisplayCancelableMaskedAppDialogEvent(event),
    [EVENT.DISPLAY_MASKED_APP_DIALOG]: event => handleDisplayMaskedAppDialog(event)
  });

  const handleCloseDialog = () => {
    setMessage(null);
    setButtonPropsCollection([]);
  };

  const handleDisplayCancelableMaskedAppDialogEvent = ({ data: { buttons: buttonPropsCollection = [], message } }) => {
    // add a cancel button to the list of buttons provided
    handleOpenDialog(message, [...buttonPropsCollection, { text: 'CANCEL' }]);
  };

  const handleDisplayMaskedAppDialog = ({ data: { buttons: buttonPropsCollection, message } }) => {
    handleOpenDialog(message, buttonPropsCollection);
  };

  const handleOpenDialog = (message, buttonPropsCollection) => {
    // if not buttons provided, show a single OK button
    const newButtonPropsCollection =
      Array.isArray(buttonPropsCollection) && buttonPropsCollection.length ? buttonPropsCollection : [{ text: 'OK' }];

    // override onClick for all buttons so they close this dialog on click
    setButtonPropsCollection(
      newButtonPropsCollection.map(({ onClick, ...remainingButtonProps }) => ({
        ...remainingButtonProps,
        onClick: () => {
          if (typeof onClick === 'function') {
            onClick();
          }
          handleCloseDialog();
        }
      }))
    );
    setMessage(message); // display triggered of message, so set this last
  };

  if (!message) {
    return null;
  }

  return (
    <div className="masked-app-dialog" data-testid="masked-app-dialog" role="dialog">
      <MaskedDialog buttons={buttonPropsCollection} className="masked-app-dialog-content" message={message} />
    </div>
  );
};

export default MaskedAppDialog;
